import React, { useState } from "react";
import axios from "axios";
import { BRAND_PRIMARY, labelStyle, inputStyle } from "../utils/constants";
import { getErrorMessage } from "../utils/formatting";

interface WhitelistEntry {
    address: string;
    label: string;
}

interface CreateAccountModalProps {
    show: boolean;
    onClose: () => void;
    onCreated: (mnemonic?: string) => void;
    authHeader: () => { headers: { Authorization: string } };
}

const modalOverlay: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.8)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: "16px",
};

const CreateAccountModal: React.FC<CreateAccountModalProps> = ({
    show,
    onClose,
    onCreated,
    authHeader,
}) => {
    const [createName, setCreateName] = useState("");
    const [createUseWhitelist, setCreateUseWhitelist] = useState(true);
    const [createWhitelist, setCreateWhitelist] = useState<WhitelistEntry[]>([
        { address: "", label: "" },
    ]);
    const [importSeed, setImportSeed] = useState(false);
    const [seedPhraseInput, setSeedPhraseInput] = useState("");
    const [seedPhraseError, setSeedPhraseError] = useState<string | null>(null);
    const [createPassword, setCreatePassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    const isCreateDisabled = () => {
        if (createLoading || !createName.trim()) return true;
        if (createPassword.length < 6 || createPassword !== confirmPassword) return true;
        if (importSeed && !seedPhraseInput.trim()) return true;
        if (createUseWhitelist) {
            const validEntries = createWhitelist.filter(
                (w) => w.address.trim() && w.label.trim()
            );
            if (validEntries.length === 0) return true;
        }
        return false;
    };

    const handleCreate = async () => {
        if (!createName.trim()) return;
        if (createPassword.length < 6) {
            setCreateError("Wallet password must be at least 6 characters");
            return;
        }
        if (createPassword !== confirmPassword) {
            setCreateError("Passwords do not match");
            return;
        }
        if (importSeed) {
            const words = seedPhraseInput.trim().split(/\s+/);
            if (words.length !== 12 && words.length !== 24) {
                setSeedPhraseError("Seed phrase must be 12 or 24 words");
                return;
            }
        }
        if (createUseWhitelist) {
            const validEntries = createWhitelist.filter(
                (w) => w.address.trim() && w.label.trim()
            );
            if (validEntries.length === 0) {
                setCreateError("At least one whitelisted address is required when whitelist is enabled");
                return;
            }
        }

        setCreateError(null);
        setSeedPhraseError(null);
        setCreateLoading(true);
        try {
            const whitelisted = createUseWhitelist
                ? createWhitelist.filter((w) => w.address.trim() && w.label.trim())
                : [];
            const payload: {
                name: string;
                useWhitelist: boolean;
                whitelistedAddresses: { address: string; label: string }[];
                walletPassword: string;
                seedPhrase?: string;
            } = {
                name: createName.trim(),
                useWhitelist: createUseWhitelist,
                whitelistedAddresses: whitelisted,
                walletPassword: createPassword,
            };
            if (importSeed && seedPhraseInput.trim()) {
                payload.seedPhrase = seedPhraseInput.trim();
            }
            const res = await axios.post("/api/accounts", payload, authHeader());

            // Reset form
            setCreateName("");
            setCreateWhitelist([{ address: "", label: "" }]);
            setCreateUseWhitelist(true);
            setImportSeed(false);
            setSeedPhraseInput("");
            setCreatePassword("");
            setConfirmPassword("");

            onClose();
            onCreated(res.data.mnemonic);
        } catch (err: unknown) {
            const errMsg = getErrorMessage(err, "Failed to create account");
            if (errMsg === "Invalid seed phrase") {
                setSeedPhraseError(errMsg);
            } else {
                setCreateError(errMsg);
            }
        } finally {
            setCreateLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div
            style={modalOverlay}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="glass-card"
                style={{
                    width: "100%",
                    maxWidth: "520px",
                    padding: "24px",
                    maxHeight: "90vh",
                    overflowY: "auto",
                }}
            >
                <h2
                    style={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: "white",
                        marginBottom: "20px",
                    }}
                >
                    Create Account
                </h2>

                {/* Account Name */}
                <div style={{ marginBottom: "16px" }}>
                    <label htmlFor="create-account-name" style={labelStyle}>Account Name</label>
                    <input
                        id="create-account-name"
                        className="input-dark"
                        style={{ ...inputStyle, width: "100%" }}
                        placeholder="e.g. Trading Account 1"
                        value={createName}
                        onChange={(e) => setCreateName(e.target.value)}
                    />
                </div>

                {/* Wallet Password */}
                <div style={{ marginBottom: "16px" }}>
                    <label htmlFor="create-account-password" style={labelStyle}>Wallet Password (min 6 characters)</label>
                    <input
                        id="create-account-password"
                        className="input-dark"
                        type="password"
                        style={{ ...inputStyle, width: "100%", marginBottom: "8px" }}
                        placeholder="Wallet password"
                        value={createPassword}
                        onChange={(e) => setCreatePassword(e.target.value)}
                    />
                    <input
                        id="create-account-confirm-password"
                        className="input-dark"
                        type="password"
                        style={{ ...inputStyle, width: "100%" }}
                        placeholder="Confirm password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                    {createPassword && confirmPassword && createPassword !== confirmPassword && (
                        <div style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
                            Passwords do not match
                        </div>
                    )}
                </div>

                {/* Seed Phrase Toggle */}
                <div style={{ marginBottom: "16px" }}>
                    <label htmlFor="create-account-seedphrase" style={labelStyle}>Seed Phrase</label>
                    <div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
                        <button
                            onClick={() => {
                                setImportSeed(false);
                                setSeedPhraseInput("");
                                setSeedPhraseError(null);
                            }}
                            style={{
                                flex: 1,
                                padding: "8px 12px",
                                borderRadius: "8px",
                                background: !importSeed
                                    ? `rgba(153, 69, 255, 0.2)`
                                    : "rgba(255,255,255,0.05)",
                                border: !importSeed
                                    ? `1px solid rgba(153, 69, 255, 0.5)`
                                    : "1px solid rgba(255,255,255,0.1)",
                                color: !importSeed ? BRAND_PRIMARY : "#9ca3af",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: !importSeed ? 600 : 400,
                            }}
                        >
                            Generate New Seed
                        </button>
                        <button
                            onClick={() => setImportSeed(true)}
                            style={{
                                flex: 1,
                                padding: "8px 12px",
                                borderRadius: "8px",
                                background: importSeed
                                    ? `rgba(153, 69, 255, 0.2)`
                                    : "rgba(255,255,255,0.05)",
                                border: importSeed
                                    ? `1px solid rgba(153, 69, 255, 0.5)`
                                    : "1px solid rgba(255,255,255,0.1)",
                                color: importSeed ? BRAND_PRIMARY : "#9ca3af",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: importSeed ? 600 : 400,
                            }}
                        >
                            Import Existing Seed
                        </button>
                    </div>
                    {importSeed && (
                        <>
                            <textarea
                                id="create-account-seedphrase"
                                className="input-dark"
                                style={{
                                    width: "100%",
                                    minHeight: "80px",
                                    fontFamily: "monospace",
                                    fontSize: "14px",
                                    padding: "10px",
                                    resize: "vertical",
                                }}
                                placeholder="Enter your 12 or 24 word seed phrase..."
                                value={seedPhraseInput}
                                onChange={(e) => {
                                    setSeedPhraseInput(e.target.value);
                                    setSeedPhraseError(null);
                                }}
                            />
                            {seedPhraseError && (
                                <div style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>
                                    {seedPhraseError}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Whitelist Checkbox */}
                <div style={{ marginBottom: "16px" }}>
                    <label
                        htmlFor="create-account-whitelist-toggle"
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            cursor: "pointer",
                            fontSize: "14px",
                            color: "#d1d5db",
                        }}
                    >
                        <input
                            id="create-account-whitelist-toggle"
                            type="checkbox"
                            checked={createUseWhitelist}
                            onChange={(e) => setCreateUseWhitelist(e.target.checked)}
                            style={{ width: "18px", height: "18px", accentColor: BRAND_PRIMARY }}
                        />
                        Use whitelist addresses
                    </label>
                    <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px", marginBottom: 0 }}>
                        {createUseWhitelist
                            ? "Only whitelisted and internal addresses can receive transfers."
                            : "Any address can be used as a transfer destination (password required for external addresses)."}
                    </p>
                </div>

                {/* Whitelist Addresses */}
                {createUseWhitelist && (
                    <div style={{ marginBottom: "20px" }}>
                        <label style={labelStyle}>
                            Whitelisted Addresses (at least 1 required)
                        </label>
                        {createWhitelist.map((entry, i) => (
                            <div
                                key={i}
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    marginBottom: "8px",
                                    alignItems: "center",
                                }}
                            >
                                <input
                                    className="input-dark"
                                    style={{ ...inputStyle, flex: 1 }}
                                    placeholder="Label"
                                    value={entry.label}
                                    onChange={(e) => {
                                        const updated = [...createWhitelist];
                                        updated[i].label = e.target.value;
                                        setCreateWhitelist(updated);
                                    }}
                                />
                                <input
                                    className="input-dark"
                                    style={{ ...inputStyle, flex: 2 }}
                                    placeholder="Solana address"
                                    value={entry.address}
                                    onChange={(e) => {
                                        const updated = [...createWhitelist];
                                        updated[i].address = e.target.value;
                                        setCreateWhitelist(updated);
                                    }}
                                />
                                {createWhitelist.length > 1 && (
                                    <button
                                        onClick={() =>
                                            setCreateWhitelist(
                                                createWhitelist.filter(
                                                    (_, j) => j !== i
                                                )
                                            )
                                        }
                                        style={{
                                            padding: "6px 10px",
                                            borderRadius: "6px",
                                            background: "rgba(239,68,68,0.15)",
                                            border: "none",
                                            color: "#ef4444",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                        }}
                                    >
                                        X
                                    </button>
                                )}
                            </div>
                        ))}
                        <button
                            onClick={() =>
                                setCreateWhitelist([
                                    ...createWhitelist,
                                    { address: "", label: "" },
                                ])
                            }
                            style={{
                                padding: "6px 12px",
                                borderRadius: "6px",
                                background: "rgba(255,255,255,0.1)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                color: "#9ca3af",
                                cursor: "pointer",
                                fontSize: "12px",
                            }}
                        >
                            + Add Address
                        </button>
                    </div>
                )}

                {createError && (
                    <div
                        style={{
                            color: "#ef4444",
                            fontSize: "13px",
                            marginBottom: "12px",
                            padding: "8px 12px",
                            background: "rgba(239,68,68,0.1)",
                            borderRadius: "8px",
                        }}
                    >
                        {createError}
                    </div>
                )}

                <div
                    style={{
                        display: "flex",
                        gap: "12px",
                        justifyContent: "flex-end",
                    }}
                >
                    <button
                        onClick={onClose}
                        style={{
                            padding: "10px 20px",
                            borderRadius: "8px",
                            background: "rgba(255,255,255,0.1)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            color: "white",
                            cursor: "pointer",
                            fontSize: "14px",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleCreate}
                        disabled={isCreateDisabled()}
                        style={{
                            padding: "10px 20px",
                            fontSize: "14px",
                            opacity: isCreateDisabled() ? 0.5 : 1,
                        }}
                    >
                        {createLoading ? "Creating..." : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateAccountModal;
